import { applyWavyAt, resetWavy } from "../../common/animation/wavyAnimation";
import { getTypewriterClipPath } from "../../common/animation/typewriterClip";
import {
  applyStyle,
  easedProgress,
  opacityProgress,
  sampleStyle,
  stagePxScale,
} from "../../common/animation/animationEngine.es6";

import {
  getTextAnimationPresets,
  getBoxAnimationPresets,
  getBoxExitAnimationPresets,
  normalizeAnimationName,
  _PAN_ENTRY_MAP,
  _PAN_EXIT_MAP,
  _resolvePanConfig,
} from "../../common/animation/presets.es6";
export {
  getTextAnimationPresets,
  getBoxAnimationPresets,
  getBoxExitAnimationPresets,
  normalizeAnimationName,
  getAnimationTargets,
} from "../../common/animation/presets.es6";

function _applyPan(outer, inner, pan, eased, compose = false) {
  outer.style.overflow = "hidden";
  _applyEngineState(inner, pan.initial, pan.animate, eased, compose);
  inner.style.transformOrigin = pan.transformOrigin;
}

// The box rotation stays outermost.
const sampledEntry = new WeakMap();
const _applyEngineState = (element, initial, animate, eased, compose = false, fade = eased) => {
  const k = stagePxScale(element);
  const style = sampleStyle(initial, animate, eased, k, fade);
  const entry = compose && sampledEntry.get(element);
  if (entry) {
    // Same order as the Visor: rotation, exit, entry.
    if (style.transform && entry.transform) style.transform += ` ${entry.transform}`;
    if (style.opacity !== undefined && entry.opacity !== undefined) style.opacity *= entry.opacity;
  }
  if (!compose) sampledEntry.set(element, style);
  applyStyle(element, style, getBaseTransform(element));
  element.style.transition = "none";
  element.style.transformOrigin = "center center";
};

export const applyTextAnimation = (element, textAnimation, currentTime) => {
  if (!element || !textAnimation) return;

  const { animation, delay = 0 } = textAnimation;
  const presets = getTextAnimationPresets();
  const animationPreset = presets.find((preset) => preset.name === animation.name);

  if (!animationPreset) {
    return;
  }

  const textContent = element.querySelector(".text-content, .plugin-content, .innerBox");
  const targetElement = textContent || element;

  const durationSec = textAnimation.duration || animationPreset.animation.transition.duration;
  if (animationPreset.name === "Type Writer") {
    targetElement.style.clipPath = getTypewriterClipPath(
      targetElement,
      easedProgress({ duration: durationSec, delay, ease: "linear" }, currentTime)
    );
  } else if (animationPreset.animation.isWavy) {
    applyWavyAt(targetElement, currentTime, durationSec, delay);
  } else {
    const transition = { ...animationPreset.animation.transition, duration: durationSec, delay };
    _applyEngineState(
      targetElement,
      animationPreset.animation.initial,
      animationPreset.animation.animate,
      easedProgress(transition, currentTime),
      false,
      opacityProgress(transition, currentTime)
    );
  }
};

export const applyBoxAnimation = (element, boxAnimation, currentTime) => {
  if (!element || !boxAnimation) return;

  const { animation, delay = 0 } = boxAnimation;
  const rawName = typeof animation === "string" ? animation : animation?.name;
  const resolvedName = normalizeAnimationName(rawName);
  const presets = getBoxAnimationPresets();
  const animationPreset = presets.find((preset) => preset.name === resolvedName);

  if (!animationPreset) {
    console.warn("[applyBoxAnimation] preset bulunamadı", {
      rawName,
      resolvedName,
      availableNames: presets.map((p) => p.name),
    });
    return;
  }

  const duration = boxAnimation.duration || animationPreset.animation.transition.duration;
  const entryTransition = { ...animationPreset.animation.transition, duration, delay };
  const eased = easedProgress(entryTransition, currentTime);
  const animationStart = delay;
  const animationEnd = delay + duration;
  let clampedProgress;
  if (currentTime < animationStart) {
    clampedProgress = 0;
  } else if (currentTime <= animationEnd) {
    clampedProgress = Math.min(1, Math.max(0, (currentTime - animationStart) / duration));
  } else {
    clampedProgress = 1;
  }

  // Type Writer: satır satır clip-path açılma (tek satırda soldan sağa inset)
  if (animationPreset.name === "Type Writer") {
    const content = element.querySelector(".boxStyle") || element;
    content.style.clipPath = getTypewriterClipPath(content, clampedProgress);
    element.style.overflow = "hidden";
    element.style.transition = "none";
    return;
  }

  if (animationPreset.animation.isWavy) {
    applyWavyAt(element.querySelector(".boxStyle") || element, currentTime, duration, delay);
    return;
  }

  // Pan (Ken Burns): outer box sabit + overflow:hidden, inner .boxStyle scale+origin ile açılır
  const _panEntry = _PAN_ENTRY_MAP[animationPreset.name];
  if (_panEntry) {
    const inner = element.querySelector(".boxStyle") || element;
    const _panAnimObj = typeof animation === "object" ? animation : boxAnimation;
    _applyPan(element, inner, _resolvePanConfig(_panEntry, _panAnimObj), eased);
    return;
  }

  const initial = animationPreset.animation.initial || {};
  const animate = animationPreset.animation.animate || {};
  if (!Object.keys(initial).length && !Object.keys(animate).length) return;
  _applyEngineState(
    element,
    initial,
    animate,
    eased,
    false,
    opacityProgress(entryTransition, currentTime)
  );
};

// currentTime: layer-relative time (from layer end), exitDuration: seconds
export const applyBoxExitAnimation = (
  element,
  boxExitAnimation,
  layerRelativeTime,
  layerDuration
) => {
  if (!element || !boxExitAnimation) return;

  const { animation } = boxExitAnimation;
  const presets = getBoxExitAnimationPresets();
  const animationPreset = presets.find(
    (p) => p.name === (typeof animation === "string" ? animation : animation?.name)
  );
  if (!animationPreset) return;
  const duration = boxExitAnimation.duration || animationPreset.animation.transition.duration;

  // Count in frames like the Visor; seconds started the exit a frame late.
  const exitStart = (layerDuration * 30 - Math.round(duration * 30)) / 30;
  if (layerRelativeTime < exitStart - 1e-6) return;

  let clampedProgress;
  if (layerRelativeTime <= layerDuration) {
    clampedProgress = Math.min(1, Math.max(0, (layerRelativeTime - exitStart) / duration));
  } else {
    clampedProgress = 1;
  }
  const exitTransition = { ...animationPreset.animation.transition, duration };
  const eased = easedProgress(exitTransition, layerRelativeTime - exitStart);

  // Type Writer Out: satır satır clip-path kapanma (son satırdan geriye doğru)
  if (animationPreset.name === "Type Writer Out") {
    const content = element.querySelector(".boxStyle") || element;
    content.style.clipPath = getTypewriterClipPath(content, 1 - clampedProgress);
    element.style.overflow = "hidden";
    element.style.transition = "none";
    return;
  }

  if (animationPreset.animation?.isWavy) {
    applyWavyAt(
      element.querySelector(".boxStyle") || element,
      layerRelativeTime - exitStart,
      duration,
      0,
      true
    );
    return;
  }

  // Pan Out (Ken Burns çıkış): outer box sabit + overflow:hidden, inner scale ile kapanır
  const _panExit = _PAN_EXIT_MAP[animationPreset.name];
  if (_panExit) {
    const inner = element.querySelector(".boxStyle") || element;
    const _panAnimObj = typeof animation === "object" ? animation : boxExitAnimation;
    // A Pan exit replaces the Pan entry.
    _applyPan(element, inner, _resolvePanConfig(_panExit, _panAnimObj), eased);
    return;
  }

  _applyEngineState(
    element,
    animationPreset.animation.initial || {},
    animationPreset.animation.animate || {},
    eased,
    true,
    opacityProgress(exitTransition, layerRelativeTime - exitStart)
  );
};

// Box rotation/flips (EditorBox's data-base-transform) — restore/compose it
// instead of wiping to "", or a rotated box snaps back to 0°.
const getBaseTransform = (element) =>
  (element && element.dataset && element.dataset.baseTransform) || "";

export const resetElementAnimationState = (element, preserveText = false) => {
  if (!element) return;

  sampledEntry.delete(element);
  resetWavy(element, preserveText);
  element.style.transform = getBaseTransform(element);
  element.style.opacity = "";
  element.style.transition = "none";
  element.style.clipPath = "";
  element.style.overflow = "";
  element.style.letterSpacing = "";

  const toReset = [
    element.querySelector(".text-content, .plugin-content, .innerBox"),
    element.querySelector(".boxStyle"),
  ];
  toReset.forEach((el) => {
    if (el && el !== element) {
      sampledEntry.delete(el);
      resetWavy(el, preserveText);
      el.style.transform = "";
      el.style.transformOrigin = "";
      // React keeps the authored Settings → Opacity on .boxStyle and won't re-apply an unchanged value.
      el.style.opacity = (el.dataset && el.dataset.baseOpacity) || "";
      el.style.transition = "none";
      el.style.clipPath = "";
      el.style.letterSpacing = "";
    }
  });
};

// Elementi bir kez animate et (panel ön izlemesi için)
export const previewBoxAnimation = (element, animName, duration, presets, panOptions = null) => {
  if (!element || !animName) return;
  const preset = presets.find((p) => p.name === animName);
  if (!preset) return;

  const basePan = _PAN_ENTRY_MAP[animName] || _PAN_EXIT_MAP[animName] || null;
  const pan = basePan ? _resolvePanConfig(basePan, panOptions) : null;
  const inner = element.querySelector(".boxStyle") || element;
  const target = pan ? inner : element;
  const initial = pan ? pan.initial : preset.animation.initial || {};
  const animate = pan ? pan.animate : preset.animation.animate || {};

  let startTime = null;
  const dur = (duration || 1) * 1000;
  let rafId = null;

  const tick = (ts) => {
    if (!startTime) startTime = ts;
    const elapsed = ts - startTime;
    const progress = Math.min(elapsed / dur, 1);
    const transition = { ...preset.animation.transition, duration: dur / 1000, delay: 0 };
    const eased = easedProgress(transition, elapsed / 1000);
    if (preset.animation.isWavy)
      applyWavyAt(inner, elapsed / 1000, dur / 1000, 0, animName.endsWith(" Out"));
    else if (animName === "Type Writer" || animName === "Type Writer Out") {
      inner.style.clipPath = getTypewriterClipPath(
        inner,
        animName.endsWith(" Out") ? 1 - progress : progress
      );
    } else if (pan) _applyPan(element, inner, pan, eased);
    else
      _applyEngineState(
        target,
        initial,
        animate,
        eased,
        false,
        opacityProgress(transition, elapsed / 1000)
      );
    if (progress < 1) rafId = requestAnimationFrame(tick);
  };

  resetElementAnimationState(element);
  if (pan) _applyPan(element, inner, pan, 0);
  else _applyEngineState(target, initial, animate, 0);
  rafId = requestAnimationFrame(tick);
  return () => {
    if (rafId) cancelAnimationFrame(rafId);
    resetElementAnimationState(element);
  };
};

export const resetAllEditorAnimations = (boxesById = {}) => {
  Object.keys(boxesById).forEach((layerId) => {
    const element = document.getElementById(`box-${layerId}`);
    if (element) {
      resetElementAnimationState(element);
      const box = boxesById[layerId];
      const timelineVisible =
        box?.timeline?.visible !== undefined ? box.timeline.visible : box?.visible;
      if (timelineVisible === false) {
        element.style.opacity = "0";
        element.style.pointerEvents = "none";
      } else {
        element.style.pointerEvents = "";
      }
    }
  });
};
